import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { backend, type AppRole } from '@/api/backend';
import { LOGIN_URL } from '@/api/config';
import { onPossibleSessionLoss, rememberReturnPath } from './sessionLoss';
import { FOCUS_CHECK_MS, readExpiry } from './expiry';
import { clearIapSession, publishAuthSignal, subscribeAuthSignals } from './authChannel';
import { ApiError } from '@/api/http';

/**
 * Authentication states, per the product brief. The backend is the authority;
 * the frontend only renders the state it is told.
 */
export type AuthStatus =
  | 'loading'
  | 'signed-out'
  | 'signing-in'
  | 'authenticated'
  | 'unauthorized-domain'
  | 'unverified-email'
  | 'expired'
  | 'revoked'
  | 'insufficient-role'
  | 'auth-unavailable';

export type Role = Lowercase<AppRole>;

export interface SessionUser {
  email: string;
  role: Role;
  /** When this session ends, so the app can warn before it does. */
  expiresAt?: string;
  /** Google profile photo from the verified identity, when the API provides it. */
  pictureUrl?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  /**
   * What the backend said when it refused, in its own words, or null.
   *
   * Live, the session probe can only tell three things apart: not signed in,
   * refused, and not answering. `unauthorized-domain`, `unverified-email`,
   * `expired` and `revoked` are real product states that no live response
   * currently reaches; they are entered in demo through `?auth=`, for design
   * review. That leaves one screen covering an account that is not enrolled,
   * one on the wrong domain, and one whose role is too low, which need
   * different things done about them. Where the API names which, that sentence
   * is shown rather than replaced with a guess.
   */
  blockedReason: string | null;
  /** True when running on fixtures with no backend. Impossible in prod builds. */
  demo: boolean;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Whether a network failure has already been seen and acted on in this tab.
 *
 * Held in session storage so it survives the full page navigation that IAP
 * sign-in performs, which is the whole point: the second failure is the one
 * that proves it is the service rather than the session.
 */
const NETWORK_FAILURE_KEY = 'poseidon.auth.networkFailure';
function sawNetworkFailure(): boolean {
  try {
    return sessionStorage.getItem(NETWORK_FAILURE_KEY) === '1';
  } catch {
    return false;
  }
}
function noteNetworkFailure(): void {
  try {
    sessionStorage.setItem(NETWORK_FAILURE_KEY, '1');
  } catch {
    // Storage refused; the loop guard is a nicety, never a requirement.
  }
}
function clearNetworkFailure(): void {
  try {
    sessionStorage.removeItem(NETWORK_FAILURE_KEY);
  } catch {
    // As above.
  }
}

/** Demo mode is a build-time flag; production builds hard-disable it. */
const DEMO = import.meta.env.VITE_DEMO !== '0';

/** For state QA: /?auth=expired etc. forces a non-success state in demo. */
function forcedState(): AuthStatus | null {
  if (!DEMO) return null;
  const v = new URLSearchParams(window.location.search).get('auth');
  const valid: AuthStatus[] = [
    'signed-out',
    'unauthorized-domain',
    'unverified-email',
    'expired',
    'revoked',
    'insufficient-role',
    'auth-unavailable',
  ];
  return valid.includes(v as AuthStatus) ? (v as AuthStatus) : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() => forcedState() ?? (DEMO ? 'signed-out' : 'loading'));
  const [user, setUser] = useState<SessionUser | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);

  const probe = useCallback(async (broadcast = true) => {
    setStatus('loading');
    setBlockedReason(null);
    try {
      const s = await backend.getSession();
      setUser({
        email: s.email ?? 'development bypass',
        role: s.role.toLowerCase() as Role,
        pictureUrl: s.pictureUrl,
        expiresAt: s.expiresAt,
      });
      setStatus('authenticated');
      clearNetworkFailure();
      // Announce to other tabs; receivers re-probe without re-broadcasting.
      if (broadcast) publishAuthSignal({ kind: 'authenticated' });
    } catch (e) {
      // Landing on a deep link with a session that died while the tab was
      // closed ends here, not in the mid-session listener below, and this is
      // the case that matters most: someone opens a saved packaging URL, is
      // sent to sign in, and used to come back to the root having lost it.
      const signedOut = () => {
        rememberReturnPath(`${window.location.pathname}${window.location.search}`);
        setStatus('signed-out');
      };
      if (e instanceof ApiError) {
        if (e.code === 'UNAUTHORIZED') signedOut();
        else if (e.code === 'FORBIDDEN') {
          // One refusal covers three different people. Carry whatever the API
          // said about which, and show it rather than deciding for it.
          // Only details.reason is contracted as operator-safe. e.message is
          // the backend's own sentence, and when it omits one the client
          // substitutes "The API returned an error without a message", which
          // is never empty, so the panel always rendered and often rendered
          // developer text under a screen promising no raw errors.
          setBlockedReason(e.reason ?? null);
          setStatus('insufficient-role');
        }
        else if (e.code === 'NETWORK') {
          // Behind IAP an unauthenticated XHR is a 302 to accounts.google.com,
          // which fetch can only surface as a network failure, so the first
          // one is treated as a signed-out session.
          //
          // A repeat is not. If the operator has already been through a
          // sign-in round trip and the probe still cannot reach the API, the
          // session is not the problem and sending them back to sign in is a
          // loop with no exit: IAP admits them instantly, they return, it
          // fails again, and nothing on screen ever names the cause.
          if (sawNetworkFailure()) setStatus('auth-unavailable');
          else {
            noteNetworkFailure();
            signedOut();
          }
        } else setStatus('auth-unavailable');
      } else setStatus('auth-unavailable');
    }
  }, []);

  useEffect(() => {
    if (!DEMO && !forcedState()) void probe();
  }, [probe]);

  // A session that dies while the app is open used to go unnoticed: the shell
  // carried on as though signed in while every panel failed, and the only way
  // to find out was to guess and reload. Any request that fails the way a dead
  // session would now raises a suspicion, and this verifies it before acting -
  // a transient outage must not sign anyone out.
  useEffect(() => {
    if (DEMO || forcedState()) return;
    let checking = false;
    return onPossibleSessionLoss(() => {
      if (checking) return;
      checking = true;
      void backend
        .getSession()
        .catch((e: unknown) => {
          // Only an answer that means "not admitted" ends the session here.
          const code = e instanceof ApiError ? e.code : null;
          if (code === 'UNAUTHORIZED' || code === 'NETWORK') {
            rememberReturnPath(`${window.location.pathname}${window.location.search}`);
            setUser(null);
            setStatus('signed-out');
            publishAuthSignal({ kind: 'signed-out' });
          }
        })
        .finally(() => {
          checking = false;
        });
    });
  }, []);

  // Cross-tab sync: apply peers' transitions; never re-broadcast from here.
  useEffect(
    () =>
      subscribeAuthSignals((sig) => {
        if (sig.kind === 'signed-out') {
          setUser(null);
          setStatus('signed-out');
        } else if (DEMO) {
          setUser({ email: 't@withmeridian.ai', role: 'reviewer' });
          setStatus('authenticated');
        } else {
          // Not probe(): its first act is setStatus('loading'), which tears
          // the shell down and renders the session splash. The publisher and
          // the subscriber are separate BroadcastChannel objects, so this tab
          // receives its own 'authenticated' message on every load, and the
          // whole app re-mounted behind a flash of "restoring your session"
          // every single time. Verify quietly and apply the result, which is
          // what the mid-session checker already does.
          void backend
            .getSession()
            .then((sess) => {
              setUser({
                email: sess.email ?? 'development bypass',
                role: sess.role.toLowerCase() as Role,
                pictureUrl: sess.pictureUrl,
                expiresAt: sess.expiresAt,
              });
              setStatus('authenticated');
            })
            .catch(() => {
              // A peer's claim that could not be confirmed changes nothing
              // here; the mid-session listener owns ending a session.
            });
        }
      }),
    [probe],
  );

  const signIn = useCallback(() => {
    if (DEMO) {
      setStatus('signing-in');
      // Simulated round-trip so the signing-in state is real, not skipped.
      setTimeout(() => {
        setUser({ email: 't@withmeridian.ai', role: 'reviewer' });
        setStatus('authenticated');
        publishAuthSignal({ kind: 'authenticated' });
      }, 600);
    } else {
      // Live: IAP sign-in is a top-level navigation to the API origin's
      // /auth/login, which 307-redirects back here after admission. Never fetch.
      // The backend refuses a returnTo in the query string on purpose, since
      // that would be an open redirect, so the destination is held here and
      // re-validated on the way out.
      rememberReturnPath(`${window.location.pathname}${window.location.search}`);
      window.location.assign(LOGIN_URL);
    }
    // Reads nothing from the session probe: the demo branch sets state and the
    // live branch navigates. `probe` was carried here and nowhere used, which
    // is the one warning the lint gate has been printing.
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    setStatus('signed-out');
    publishAuthSignal({ kind: 'signed-out' });
    if (!DEMO) clearIapSession();
  }, []);

  /**
   * Renew the IAP assertion quietly, before it lapses.
   *
   * `expiresAt` is the assertion's end, not the session's, and IAP mints a
   * fresh one on the next request. Nothing needs to be announced and nobody
   * needs to press anything: this re-reads the session shortly before the
   * assertion lapses, and on a window that has been left alone and comes back.
   *
   * It does not set `loading`, because that blanks the app - a refresh an
   * operator can see is not a quiet one. The only outcome that changes
   * anything is a refusal, and `probe`'s own handling covers that: a genuine
   * UNAUTHORIZED is what sends someone to sign in, which is the only honest
   * proof that a session has ended.
   */
  const renew = useCallback(async () => {
    try {
      const s = await backend.getSession();
      setUser((was) => ({
        email: s.email ?? was?.email ?? 'development bypass',
        role: (s.role.toLowerCase() as Role) ?? was?.role,
        pictureUrl: s.pictureUrl,
        expiresAt: s.expiresAt,
      }));
    } catch (e) {
      // A refused session read IS the verification, so this acts directly. It
      // must end the session the same way the verifier does, though: the user
      // was left in place and peer tabs were never told, so a second tab kept
      // showing a signed-in shell until it happened to make its own request.
      if (e instanceof ApiError && (e.code === 'UNAUTHORIZED' || e.code === 'NETWORK')) {
        rememberReturnPath(`${window.location.pathname}${window.location.search}`);
        setUser(null);
        setStatus('signed-out');
        publishAuthSignal({ kind: 'signed-out' });
      }
      // Anything else is a passing fault. The assertion is renewed on the next
      // request anyway, and tearing the app down over one failed read would be
      // the same mistake as the countdown.
    }
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const { at, renewInMs } = readExpiry(user?.expiresAt);
    const t = at ? setTimeout(() => void renew(), renewInMs) : null;

    /**
     * Coming back to a tab checks the session, every time.
     *
     * This used to check only when the assertion was near lapsing, which made
     * the most ordinary case in the product - leave a tab open, come back an
     * hour later - depend on where a short clock happened to be. Returning to
     * a dead session showed a working page until something was pressed.
     *
     * Throttled, because focus fires on every alt-tab and this is a request.
     */
    let checkedAt = 0;
    const onReturn = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - checkedAt < FOCUS_CHECK_MS) return;
      checkedAt = now;
      void renew();
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [status, user?.expiresAt, renew]);

  const value = useMemo(
    () => ({ status, user, blockedReason, demo: DEMO, signIn, signOut }),
    [status, user, blockedReason, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
